import React from 'react';

import { useIntl } from 'react-intl';

import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/useToast';
import { useParams } from 'react-router-dom';
import ServicePlanPricingPolicyFormModal from 'src/features/servicePlanPricingPolicies/components/forms/ServicePlanPricingPolicyFormModal';
import { LIST_SERVICE_PLAN_PRICING_POLICIES_RESOURCE } from 'src/features/servicePlanPricingPolicies/api/useListServicePlanPricingPolicies';
import { pricingPoliciesMessages } from 'src/features/servicePlanPricingPolicies/utils/messages';
import { ServicePlanItemPricingPolicy } from 'src/features/servicePlanPricingPolicies/utils/types';
import { usePatchServicePlanPricingPolicy } from 'src/features/servicePlanPricingPolicies/api/usePatchServicePlanPricingPolicy';

interface UpdateServicePlanPricingPolicyFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedServicePlanPricingPolicy: ServicePlanItemPricingPolicy;
    children?: React.ReactNode;
}

function UpdateServicePlanPricingPolicyFormModal<TData>({
    className,
    semantics,
    children,
    selectedServicePlanPricingPolicy,
}: UpdateServicePlanPricingPolicyFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { servicePlanItemId = '' } = useParams();

    const { mutate, isPending } = usePatchServicePlanPricingPolicy({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SERVICE_PLAN_PRICING_POLICIES_RESOURCE] });
            toast({
                title: formatMessage(pricingPoliciesMessages.updateServicePlanPricingPolicySuccess),
                description: formatMessage(pricingPoliciesMessages.updateServicePlanPricingPolicySuccessDescription),
            });
        },
    });

    return (
        <ServicePlanPricingPolicyFormModal
            selectedServicePlanPricingPolicy={selectedServicePlanPricingPolicy}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate(
                    { ...values, servicePlanItemId, pricingPolicyId: selectedServicePlanPricingPolicy.id },
                    { onSuccess, onError },
                )
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(pricingPoliciesMessages.editServicePlanPricingPolicySubtitle)}
        >
            {children}
        </ServicePlanPricingPolicyFormModal>
    );
}

export default UpdateServicePlanPricingPolicyFormModal;
